import CheckIcon from '@mui/icons-material/Check';
import CreateIcon from '@mui/icons-material/Create';
import { IconButton, Tooltip } from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilStateLoadable } from 'recoil';

import { useGetAuditMetaDataQuery } from '../../api/apigQuery';
import { IUserAtom, user } from '../../recoil/atoms/user';
import EditAuditMetaDataModal from '../components/modals/EditAuditMetaDataModal';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles(() => ({
  fullWidth: {
    width: '100% !important',
  },
  centerColumn: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignContent: 'center',
    width: '100%',
  },
  checkContainer: {
    color: green[500],
  },
}));

const AuditMetaData = () => {
  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [selected, setSelected] = useState<any>([]);
  const [showEdit, setShowEdit] = useState<any>(false);
  const [search, _setSearch] = useState<string>('');
  const classes = useStyles();

  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    {
      field: 'date',
      sort: 'desc',
    },
  ]);

  const handleGridSortModelChange = (model: GridSortModel, details?: any) => {
    setGridSortModel(model);
  };

  const {
    data: auditMetaData,
    isFetching: auditMetaDataLoading,
    refetch: refetchAuditMetaData,
  } = useGetAuditMetaDataQuery({
    userSettings: {
      businessid: userAtomContents?.businessid,
      businessId: userAtomContents?.businessid,
      userId: userAtomContents?.businessid,
      userid: userAtomContents?.businessid,
      apikey: userAtomContents?.apikey,
    },
    tableName: '*',
  });

  const rows = useMemo(
    () =>
      auditMetaDataLoading
        ? []
        : auditMetaData?.map((el, i) => ({
            id: el?.auditMetaDataId,
            ...el,
          })),
    [auditMetaDataLoading, auditMetaData]
  );

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
        enableSorting: true,
      },
      {
        field: 'tableName',
        headerName: 'Table Name',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
        enableSorting: true,
      },
      {
        field: 'columnName',
        headerName: 'Column Name',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
        enableSorting: true,
      },
      {
        field: 'fieldLabel',
        headerName: 'Field Label',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
        editable: true,
        enableSorting: true,
      },
      {
        field: 'isAuditable',
        headerName: 'Is Auditable',
        headerClassName: classes.fullWidth,
        cellClassName: clsx(
          classes.fullWidth,
          classes.centerColumn,
          classes.checkContainer
        ),
        align: 'center',
        headerAlign: 'center',
        enableSorting: true,
        renderCell: (params) =>
          params.value === 1 ? <CheckIcon color="inherit" /> : <></>,
      },
      {
        field: 'internalViewOnly',
        headerName: 'Internal View Only',
        headerClassName: classes.fullWidth,
        cellClassName: clsx(
          classes.fullWidth,
          classes.centerColumn,
          classes.checkContainer
        ),
        enableSorting: true,
        headerAlign: 'center',
        renderCell: (params) =>
          params.value === 1 ? <CheckIcon color="inherit" /> : <></>,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 85,
        headerAlign: 'center',
        align: 'center',
        disableExport: true,
        enableSorting: false,
        renderCell: (params) => {
          return (
            <div className={classes.centerColumn}>
              <Tooltip title="Edit">
                <IconButton
                  name="edit"
                  onClick={() => {
                    setSelected(params.row.data);
                    setShowEdit(true);
                  }}
                  size="large"
                >
                  <CreateIcon color="primary" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [rows, setSelected, setShowEdit, classes]
  );
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        search={search}
        isLoading={auditMetaDataLoading}
      />
    ),
    [auditMetaData, search, rows, auditMetaDataLoading]
  );
  return (
    <>
      <DataTable
        ToolbarComponent={ToolbarComponent}
        rows={rows ?? []}
        columns={columns}
        loading={auditMetaDataLoading}
        sortModel={sortModel}
        onSortModelChange={handleGridSortModelChange}
      />
      {showEdit && (
        <EditAuditMetaDataModal
          show={showEdit}
          handleClose={() => {
            setShowEdit(false);
            setSelected(false);
          }}
          setRefetchToggleForAuditMetaData={refetchAuditMetaData}
          row={selected}
          userAtomContents={userAtomContents}
        />
      )}
    </>
  );
};

export default AuditMetaData;
